import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TiptapLink from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import { Button } from "@/components/ui/button";
import {
  Loader2, Send, Mail, ChevronDown, ChevronUp,
  Bold, Italic, Strikethrough, List, ListOrdered,
  Link as LinkIcon, Undo, Redo, Check, Eye, MousePointerClick, AlertTriangle,
  ArrowUpRight, ArrowDownLeft, Pencil,
} from "lucide-react";
import { API } from "@/lib/admin-constants";

const MiniToolbar = ({ editor }) => {
  if (!editor) return null;
  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const btns = [
    { id: "bold", action: () => editor.chain().focus().toggleBold().run(), active: "bold", icon: Bold },
    { id: "italic", action: () => editor.chain().focus().toggleItalic().run(), active: "italic", icon: Italic },
    { id: "strike", action: () => editor.chain().focus().toggleStrike().run(), active: "strike", icon: Strikethrough },
    { id: "d1", divider: true },
    { id: "bullet", action: () => editor.chain().focus().toggleBulletList().run(), active: "bulletList", icon: List },
    { id: "ordered", action: () => editor.chain().focus().toggleOrderedList().run(), active: "orderedList", icon: ListOrdered },
    { id: "d2", divider: true },
    { id: "link", action: setLink, active: "link", icon: LinkIcon },
    { id: "d3", divider: true },
    { id: "undo", action: () => editor.chain().focus().undo().run(), disabled: !editor.can().undo(), icon: Undo },
    { id: "redo", action: () => editor.chain().focus().redo().run(), disabled: !editor.can().redo(), icon: Redo },
  ];
  return (
    <div className="flex flex-wrap gap-0.5 p-1.5 border-b border-gray-200 bg-gray-50">
      {btns.map((b) =>
        b.divider ? (
          <div key={b.id} className="w-px h-5 bg-gray-200 mx-0.5" />
        ) : (
          <button
            key={b.id}
            type="button"
            onClick={b.action}
            disabled={b.disabled}
            className={`h-6 w-6 flex items-center justify-center rounded text-xs transition-colors ${
              b.active && editor.isActive(b.active) ? "bg-cfc-primary text-white" : "hover:bg-gray-200 text-gray-500"
            } ${b.disabled ? "opacity-30" : ""}`}
          >
            <b.icon size={13} />
          </button>
        )
      )}
    </div>
  );
};

const EventBadges = ({ msg }) => {
  if (!msg.event_types || msg.event_types.length === 0) return null;
  const badges = [];
  if (msg.event_types.includes("delivered"))
    badges.push({ icon: Check, label: "Delivered", cls: "text-green-600" });
  if (msg.event_types.includes("open"))
    badges.push({ icon: Eye, label: "Opened", cls: "text-blue-600" });
  if (msg.event_types.includes("click"))
    badges.push({ icon: MousePointerClick, label: "Clicked", cls: "text-purple-600" });
  if (msg.event_types.includes("bounce") || msg.event_types.includes("dropped"))
    badges.push({ icon: AlertTriangle, label: "Bounced", cls: "text-red-600" });
  if (badges.length === 0) return null;
  return (
    <div className="flex items-center gap-2 mt-1.5">
      {badges.map((b, i) => (
        <span key={b.id} className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${b.cls}`}>
          <b.icon size={10} /> {b.label}
        </span>
      ))}
    </div>
  );
};

const formatTime = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const EmailThread = ({ leadId }) => {
  const [messages, setMessages] = useState([]);
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [expandedMsgs, setExpandedMsgs] = useState({});
  const bottomRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapLink.configure({ openOnClick: false }),
      Highlight,
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none px-3 py-2 focus:outline-none",
        style: "min-height: 80px",
      },
    },
  });

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email/conversations/${leadId}`, { withCredentials: true });
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error("Failed to fetch email messages:", error);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchMessages();
    // Mark emails as read when viewing
    axios.post(`${API}/email/mark-read/${leadId}`, {}, { withCredentials: true }).catch(() => {});
    const interval = setInterval(fetchMessages, 15000);
    return () => clearInterval(interval);
  }, [fetchMessages, leadId]);

  useEffect(() => {
    if (!initialLoad && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [messages, initialLoad]);

  const sendEmail = async () => {
    if (!editor || !subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    const htmlBody = editor.getHTML();
    const textContent = editor.getText();
    if (!textContent.trim()) {
      toast.error("Message body is required");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/email/send/${leadId}`, {
        subject: subject.trim(),
        body: htmlBody,
        body_text: textContent,
      }, { withCredentials: true });
      editor.commands.clearContent();
      setSubject("");
      setShowCompose(false);
      await fetchMessages();
      toast.success("Email sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const toggleExpand = (msgId) => {
    setExpandedMsgs((prev) => ({ ...prev, [msgId]: !prev[msgId] }));
  };

  const isHtml = (str) => /<[a-z][\s\S]*>/i.test(str);

  const renderBody = (msg) => {
    const body = msg.body || "";
    const expanded = expandedMsgs[msg.message_id];
    const isLong = body.length > 300;

    if (isHtml(body)) {
      return (
        <div>
          <div
            className={`prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline ${!expanded && isLong ? "max-h-20 overflow-hidden" : ""}`}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body) }}
          />
          {isLong && (
            <button onClick={() => toggleExpand(msg.message_id)} className="text-[11px] text-cfc-primary font-medium mt-1 flex items-center gap-0.5 hover:underline">
              {expanded ? <><ChevronUp size={11} /> Less</> : <><ChevronDown size={11} /> More</>}
            </button>
          )}
        </div>
      );
    }
    const displayText = !expanded && isLong ? body.slice(0, 300) + "..." : body;
    return (
      <div>
        <p className="whitespace-pre-wrap leading-relaxed">{displayText}</p>
        {isLong && (
          <button onClick={() => toggleExpand(msg.message_id)} className="text-[11px] text-cfc-primary font-medium mt-1 flex items-center gap-0.5 hover:underline">
            {expanded ? <><ChevronUp size={11} /> Less</> : <><ChevronDown size={11} /> More</>}
          </button>
        )}
      </div>
    );
  };

  return (
    <div data-testid="email-thread">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-cfc-primary/10 flex items-center justify-center">
            <Mail size={13} className="text-cfc-primary" />
          </div>
          <span className="font-semibold text-cfc-primary text-sm">Emails</span>
          {messages.length > 0 && (
            <span className="text-[10px] bg-cfc-primary/10 text-cfc-primary font-medium px-1.5 py-0.5 rounded-full">{messages.length}</span>
          )}
        </div>
        <Button
          size="sm"
          variant={showCompose ? "ghost" : "default"}
          className={showCompose ? "text-xs text-gray-500 h-7" : "text-xs h-7 bg-cfc-primary hover:bg-cfc-primary-hover"}
          onClick={() => setShowCompose(!showCompose)}
          data-testid="compose-email-toggle"
        >
          {showCompose ? "Cancel" : <><Pencil size={12} className="mr-1" /> New Email</>}
        </Button>
      </div>

      {/* Compose */}
      {showCompose && (
        <div className="rounded-lg border border-cfc-primary/20 mb-3 bg-white shadow-sm overflow-hidden" data-testid="email-compose">
          <div className="bg-cfc-primary/5 px-3 py-2 border-b border-cfc-primary/10">
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject (required)"
              className="w-full bg-transparent text-sm font-medium text-cfc-primary placeholder:text-gray-400 focus:outline-none"
              autoFocus
              data-testid="email-subject-input"
            />
          </div>
          <MiniToolbar editor={editor} />
          <EditorContent editor={editor} data-testid="email-body-editor" />
          <div className="flex justify-between items-center px-3 py-2 border-t border-gray-100 bg-gray-50/50">
            <span className="text-[10px] text-gray-400">Formatting: Bold, Italic, Lists, Links</span>
            <Button
              size="sm"
              onClick={sendEmail}
              disabled={sending}
              className="h-7 bg-cfc-primary hover:bg-cfc-primary-hover text-xs px-4"
              data-testid="email-send-btn"
            >
              {sending ? <Loader2 size={13} className="animate-spin mr-1" /> : <Send size={13} className="mr-1" />}
              Send
            </Button>
          </div>
        </div>
      )}

      {/* Thread */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 size={18} className="animate-spin text-gray-300" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Mail size={24} className="mx-auto mb-2 text-gray-300" />
              <p className="text-xs text-gray-400">No email conversation yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {messages.map((msg, idx) => {
                const isOutbound = msg.direction === "outbound";
                return (
                  <div
                    key={msg.message_id || idx}
                    className="group hover:bg-gray-50/50 transition-colors"
                    data-testid={`email-msg-${msg.direction}`}
                  >
                    {/* Message header row */}
                    <div className="flex items-start gap-2.5 px-3.5 pt-3 pb-1">
                      {/* Direction icon */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                        isOutbound ? "bg-emerald-100" : "bg-blue-100"
                      }`}>
                        {isOutbound
                          ? <ArrowUpRight size={12} className="text-emerald-600" />
                          : <ArrowDownLeft size={12} className="text-blue-600" />
                        }
                      </div>
                      {/* From/To + time */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="min-w-0">
                            <span className="text-xs font-semibold text-gray-800">
                              {isOutbound ? "You" : (msg.from_email || "Unknown")}
                            </span>
                            <span className="text-[10px] text-gray-400 ml-1.5">
                              {isOutbound ? `to ${msg.to_email}` : ""}
                            </span>
                          </div>
                          <span className="text-[10px] text-gray-400 shrink-0 tabular-nums">
                            {formatTime(msg.timestamp)}
                          </span>
                        </div>
                        {/* Subject — only show if different from previous */}
                        {msg.subject && (idx === 0 || msg.subject !== messages[idx - 1]?.subject) && (
                          <p className="text-[11px] text-gray-500 font-medium mt-0.5 truncate">{msg.subject}</p>
                        )}
                      </div>
                    </div>
                    {/* Message body */}
                    <div className="pl-12 pr-3.5 pb-3 text-[13px] text-gray-700 leading-relaxed">
                      {renderBody(msg)}
                      {isOutbound && <EventBadges msg={msg} />}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>
    </div>
  );
};

export default EmailThread;
